import React, { ComponentType, Fragment, useMemo } from 'react';
import buildsEnhancedMarkdownOverrides from '@resources/js/helpers/buildsEnhancedMarkdownOverrides';
import EnhancedMarkdown from '@resources/js/components/EnhancedMarkdown/EnhancedMarkdown';
import useBuildsRotationFacets from '@resources/js/contexts/useBuildsRotationFacets';
import VerticalContent from '@resources/js/components/VerticalContent/VerticalContent';
import BuildsNoDataPlaceholder from '@resources/js/components/BuildsNoDataPlaceholder/BuildsNoDataPlaceholder';
import EventTimelineGrid, {
  EventTimelineGridViewModel,
} from '@resources/js/components/EventTimelineGrid/EventTimelineGrid';
import { useIsFullscreen } from '@resources/js/hooks/useIsFullscreen';

export type BuildsCooldownsSectionViewModel = {
  title: string;
  description: string;
  eventTimelineGridsByFacetId: Record<string, EventTimelineGridViewModel>;
  Wrapper?: ComponentType;
};

export default function BuildsCooldownsSection({
  title,
  description,
  eventTimelineGridsByFacetId,
  Wrapper,
}: BuildsCooldownsSectionViewModel): JSX.Element | null {
  const { facet } = useBuildsRotationFacets();
  const facetToFilterTo =
    facet?.value ?? Object.keys(eventTimelineGridsByFacetId)[0];
  const eventTimelineGridForFacet = useMemo(
    () =>
      facetToFilterTo
        ? eventTimelineGridsByFacetId[facetToFilterTo]
        : undefined,
    [eventTimelineGridsByFacetId, facetToFilterTo]
  );
  const { isFullscreen } = useIsFullscreen();

  if (!title) {
    return null;
  }

  if (!eventTimelineGridForFacet) {
    return (
      <VerticalContent className='builds-cooldowns-section'>
        <EnhancedMarkdown
          markdown={`
# ${title}

${description}
`}
          overrides={buildsEnhancedMarkdownOverrides}
        />
        <BuildsNoDataPlaceholder />
      </VerticalContent>
    );
  }

  Wrapper ??= Fragment;

  return (
    <Wrapper>
      <VerticalContent className='builds-cooldowns-section'>
        <EnhancedMarkdown
          markdown={`
# ${title}

${description}
`}
          overrides={buildsEnhancedMarkdownOverrides}
        />
        <EventTimelineGrid
          {...eventTimelineGridForFacet}
          key={description}
          limit={isFullscreen ? undefined : 10}
        />
      </VerticalContent>
    </Wrapper>
  );
}
